import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Button,
  IconButton,
  Typography,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import './ToggleTypesOfSession.css';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

const ToggleTypesOfSession = ({ open, onClose, sessionType, selectedIds, onConfirm }) => {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date(new Date().setMonth(new Date().getMonth() + 3))); // Default 3 months from now
  const [isIndefinite, setIsIndefinite] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Reset dates when modal opens
  useEffect(() => {
    if (open) {
      setStartDate(new Date());
      setEndDate(new Date(new Date().setMonth(new Date().getMonth() + 3)));
      setIsIndefinite(false);
      setShowDatePicker(false);
    }
  }, [open]);

  const handleEnable = () => {
    // For Prime sessions, show date picker first
    if (sessionType === 'prime' && !showDatePicker) {
      setShowDatePicker(true);
      return;
    }

    // For Prime sessions with date picker already shown, validate dates
    if (sessionType === 'prime' && showDatePicker) {
      if (!startDate) {
        toast.error('Please select a start date');
        return;
      }

      if (!isIndefinite && !endDate) {
        toast.error('Please select an end date or check indefinite');
        return;
      }

      // Get end date (either selected or last day of century if indefinite)
      const finalEndDate = isIndefinite ? new Date('2099-12-31') : endDate;

      // Call parent's onConfirm with the dates
      onConfirm({
        sessionType,
        isEnabling: true,
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(finalEndDate, 'yyyy-MM-dd'),
        isIndefinite,
      });
    } else {
      // For Group sessions, just confirm with session type
      onConfirm({
        sessionType,
        isEnabling: true,
      });
    }
    onClose();
  };

  const handleDisable = () => {
    // For both session types, just confirm disable action
    onConfirm({
      sessionType,
      isEnabling: false,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth className="toggle-session-dialog">
      <DialogTitle className="toggle-session-title">
        Manage {sessionType === 'group' ? 'Group' : 'Prime'} Sessions
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
          className="toggle-session-close-btn"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="toggle-session-content">
        <DialogContentText className="toggle-session-text">
          Select an action for {sessionType} sessions for the selected instructors (
          {selectedIds.size}).
        </DialogContentText>

        {sessionType === 'prime' && showDatePicker && (
          <Box className="toggle-prime-date-container">
            <Typography className="toggle-session-subtitle">
              Set availability period for Prime Sessions
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box className="date-pickers-container">
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={newValue => setStartDate(newValue)}
                  renderInput={params => <TextField {...params} fullWidth />}
                  disablePast
                  className="date-picker"
                />

                {!isIndefinite && (
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={newValue => setEndDate(newValue)}
                    renderInput={params => <TextField {...params} fullWidth />}
                    disablePast
                    minDate={startDate}
                    className="date-picker"
                  />
                )}
              </Box>
            </LocalizationProvider>

            <FormControlLabel
              control={
                <Checkbox
                  checked={isIndefinite}
                  onChange={e => setIsIndefinite(e.target.checked)}
                  className="indefinite-checkbox"
                />
              }
              label="Indefinite (No end date)"
              className="indefinite-label"
            />
          </Box>
        )}

        <Box className="toggle-session-actions">
          <Button
            onClick={handleDisable}
            variant="contained"
            color="error"
            startIcon={<BlockIcon />}
            className="action-button-tts disable-button"
          >
            Disable {sessionType === 'group' ? 'Group' : 'Prime'} Sessions
          </Button>

          <Button
            onClick={handleEnable}
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            className="action-button-tts enable-button"
          >
            Enable {sessionType === 'group' ? 'Group' : 'Prime'} Sessions
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ToggleTypesOfSession;
